import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ThumbsUp, ThumbsDown, ExternalLink } from "lucide-react";
import BoringAvatar from "boring-avatars";

interface FeedbackItem {
  id: number;
  created_at: string;
  updated_at?: string;
  help_center?: { title: string; slug?: string };
  user_hash?: string;
  feedback_type: string;
  item?: { title: string; slug?: string };
  item_id: number;
  rating: number | null;
  issue_types?: string[];
  feedback_text?: string;
  browser_info?: {
    browser?: string;
    os?: string;
    screen?: string;
    device?: string;
  };
}

interface FeedbackDetailsDrawerProps {
  feedback: FeedbackItem | null;
  isOpen: boolean;
  onClose: () => void;
  formatDate: (dateString: string) => string;
  getRatingIcon: (rating: number | null) => React.ReactNode;
  getItemUrl: (item: FeedbackItem) => string | null;
  onOpenItem: (url: string) => void;
}

export const FeedbackDetailsDrawer: React.FC<FeedbackDetailsDrawerProps> = ({
  feedback,
  isOpen,
  onClose,
  formatDate,
  getRatingIcon,
  getItemUrl,
  onOpenItem,
}) => {
  if (!feedback) return null;
 /*
         <div className="font-medium">
                
                </div>
                */
  const itemUrl = getItemUrl(feedback);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>Feedback</SheetTitle>
          <SheetDescription>
            Left at {formatDate(feedback.created_at)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="grid grid-cols-2 gap-2">
            {/* User Information */}
            {feedback.user_hash && (
              <div>
                <h3 className="text-sm font-semibold mb-2">User</h3>
                <div className="flex items-center gap-3">
                  <BoringAvatar
                    name={feedback.user_hash}
                    variant="beam"
                    size={48}
                  />
                  <div>
                    <div className="text-sm font-medium">Anonymous User</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {feedback.user_hash}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Item Information */}
            <div>
              <h3 className="text-sm font-semibold mb-2">Item</h3>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{feedback.feedback_type}</Badge>
                  <span className="text-sm font-medium">
                    {feedback.item?.title || `ID: ${feedback.item_id}`}
                  </span>
                  {itemUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2"
                      onClick={() => onOpenItem(itemUrl)}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Open
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <h3 className="text-sm font-semibold mb-2">Rating</h3>
              <div className="flex items-center gap-2">
                {getRatingIcon(feedback.rating)}
                <span className="text-sm">
                  {feedback.rating === 1
                    ? "Positive (Thumbs Up)"
                    : feedback.rating === 0
                    ? "Negative (Thumbs Down)"
                    : "No Rating"}
                </span>
              </div>
            </div>

            {/* Issue Types */}
            {feedback.issue_types && feedback.issue_types.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Issue Types</h3>
                <div className="space-y-2">
                  {feedback.issue_types.map((issue: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="text-xs bg-gray-100 whitespace-pre-wrap bg-muted/50 p-3 rounded-md">
                        {issue}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Feedback Text */}
          {feedback.feedback_text && (
            <>
              <div>
                <h3 className="text-sm font-semibold mb-2">Feedback Text</h3>
                <p className="text-sm text-foreground bg-gray-100 whitespace-pre-wrap bg-muted/50 p-3 rounded-md">
                  {feedback.feedback_text}
                </p>
              </div>
              <Separator />
            </>
          )}

          {/* Browser Information */}
          {feedback.browser_info && (
            <div>
              <h3 className="text-sm font-semibold mb-2">
                Browser Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-muted-foreground">Browser:</span>
                    <div className="font-medium">
                      {feedback.browser_info.browser || "Unknown"}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">OS:</span>
                    <div className="font-medium">
                      {feedback.browser_info.os || "Unknown"}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Screen:</span>
                    <div className="font-medium">
                      {feedback.browser_info.screen || "Unknown"}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Device:</span>
                    <div className="font-medium">
                      {feedback.browser_info.device || "Unknown"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Metadata */}
        </div>
      </SheetContent>
    </Sheet>
  );
};
